import { DownloadCloud, Loader2 } from 'lucide-react';
import { useState } from 'react';

import { StateCitySelect } from '@/components/sales/wizard/StateCitySelect';
import { Field, Section } from '@/components/sales/wizard/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { type LeadDetailsForm } from '@/features/sales-rm/leadForm';
import { formatValidTill, kydLabel } from '@/lib/sales-metrics';
import { normalizeArn } from '@/utils/arn';
import cn from '@/utils/cn';

interface StepLeadDetailsProps {
  form: LeadDetailsForm;
  setField: <K extends keyof LeadDetailsForm>(k: K, v: LeadDetailsForm[K]) => void;
  onAutoPopulate: () => void;
  isFetching: boolean;
  onAutoPopulateGst: () => void;
  isFetchingGst: boolean;
  onSaveProceed: () => void;
  isDone: boolean;
  // Client type (ADKJ-117). Non-ARN clients have a system code instead of an
  // AMFI number, so the ARN fetch is hidden and the AUM field is irrelevant.
  isNonArn: boolean;
  clientCode: string;
  // Switch the client type. Converting to ARN holder requires the real ARN.
  onChangeClientType: (isNonArn: boolean, arn?: string) => void;
  isChangingClientType: boolean;
  // AMFI facts about this ARN (Fix 1), READ-ONLY. Written server-side by the ARN
  // lookup and by nothing else, so they arrive here as values to display, never
  // as form state - there is no setter for them and no payload that carries them.
  // `kydCompliant: null` means "never fetched", which is a different statement
  // from `false` ("the registry says this ARN holder is not compliant").
  kydCompliant: boolean | null;
  // 'YYYY-MM-DD', a calendar date rather than an instant.
  arnValidTill: string | null;
}

export function StepLeadDetails({
  form,
  setField,
  onAutoPopulate,
  isFetching,
  onAutoPopulateGst,
  isFetchingGst,
  onSaveProceed,
  isDone,
  isNonArn,
  clientCode,
  onChangeClientType,
  isChangingClientType,
  kydCompliant,
  arnValidTill,
}: StepLeadDetailsProps) {
  // Converting a Non-ARN client to an ARN holder needs their real ARN first;
  // this holds the inline input that collects it.
  const [pendingArn, setPendingArn] = useState<string | null>(null);
  const [arnError, setArnError] = useState<string | null>(null);
  // The client type the RM has asked for and not yet confirmed. Neither
  // direction is a display preference - Non-ARN retires the AMFI number for a
  // minted 'NA…' code and takes the ARN-only products with it - so the switch
  // goes through a confirmation rather than firing on a single click.
  const [pendingType, setPendingType] = useState<boolean | null>(null);

  const selectType = (nextIsNonArn: boolean) => {
    if (isChangingClientType) return;
    if (nextIsNonArn === isNonArn) {
      setPendingArn(null);
      return;
    }
    setPendingType(nextIsNonArn);
  };

  // What the dialog SAYS, held across the close animation. Radix keeps the
  // content mounted while it fades out, and both Confirm and Cancel clear
  // `pendingType` first - so reading that directly made the dialog flip to
  // "Switch to ARN holder?" on its way out of a Non-ARN confirmation.
  const askingForNonArn = pendingType ?? !isNonArn;

  const confirmTypeSwitch = () => {
    const next = pendingType;
    setPendingType(null);
    if (next === null) return;
    if (next) {
      onChangeClientType(true);
      return;
    }
    // -> ARN holder: collect the ARN before committing the switch.
    setPendingArn('');
    setArnError(null);
  };

  const applyArnConversion = () => {
    const normalized = normalizeArn(pendingArn ?? '');
    if (!normalized || normalized.startsWith('NA')) {
      setArnError('Enter a valid ARN (1 to 6 digits)');
      return;
    }
    setArnError(null);
    setPendingArn(null);
    onChangeClientType(false, normalized);
  };

  // One fixed width for BOTH options, so the two pills are the same size and the
  // control does not resize as the active pill moves between two labels of
  // different length. `px-3` stays off it on purpose: padding on a fixed-width
  // button only eats into the label, and "ARN Holder" is the longer of the two.
  const typeOptionClass = (active: boolean): string =>
    cn(
      'w-28 shrink-0 rounded-md py-1.5 text-center text-xs font-semibold transition-colors',
      active ? 'bg-primary text-white shadow-sm' : 'text-muted-foreground hover:text-foreground',
    );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold">Lead Details</h3>
          <p className="text-xs text-muted-foreground">
            {isNonArn
              ? `Non-ARN client · code ${clientCode}. Fill the details manually - there is no AMFI record to fetch.`
              : 'Click Fetch from ARN to populate details automatically. All fields are editable.'}
          </p>
          {/* Fix 6: these were 11px muted-on-card and effectively unreadable.
              Bumped to the body size and the full foreground colour, with only
              the label left muted so the pair still reads as label/value. */}
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm font-medium text-foreground">
            <span>
              <span className="font-semibold text-muted-foreground">Created:</span>{' '}
              {form.createdDate ? new Date(form.createdDate).toLocaleString() : '-'}
            </span>
            <span>
              <span className="font-semibold text-muted-foreground">Modified:</span>{' '}
              {form.modifiedDate ? new Date(form.modifiedDate).toLocaleString() : '-'}
            </span>
          </div>
        </div>
        {/*
          Fix 6 - nothing in this cluster may move when it is clicked.

          Three separate things used to shift it, and each is pinned here:
            1. The busy spinner was INSERTED into the toggle, widening it. It now
               sits outside the bordered group in a slot that is always present;
               only its contents toggle.
            2. "Fetch from ARN" became the narrower "Fetching…", so the button -
               and everything after it - resized mid-request. It carries a fixed
               width that fits the longer of its two labels.
            3. The ARN button UNMOUNTED when the client type switched to
               Non-ARN, collapsing the row. Its slot is reserved and the button
               is only made invisible, so the row keeps its height and the
               toggle stays exactly where it was. `aria-hidden` +
               `tabIndex={-1}` + `pointer-events-none` keep it out of the tab
               order and out of the accessibility tree, so it is hidden in every
               sense except layout.
          Fetch from GST is no longer here: it belongs to the Business section
          and now sits on that section's heading row.
        */}
        <div className="flex flex-wrap items-center gap-2">
          {/*
            Client type toggle (ADKJ-117). The busy spinner sits OUTSIDE the
            bordered group: inside it, its slot padded the Non-ARN end of the
            control and made that half read as the wider of the two.
          */}
          <div className="inline-flex items-center gap-2">
            <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-muted/40 p-1">
              <button
                type="button"
                aria-pressed={!isNonArn}
                className={typeOptionClass(!isNonArn)}
                onClick={() => selectType(false)}
              >
                ARN Holder
              </button>
              <button
                type="button"
                aria-pressed={isNonArn}
                className={typeOptionClass(isNonArn)}
                onClick={() => selectType(true)}
              >
                Non-ARN
              </button>
            </div>
            <span className="grid h-3.5 w-3.5 shrink-0 place-items-center" aria-hidden>
              {isChangingClientType && (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
              )}
            </span>
          </div>
          <Button
            size="sm"
            onClick={onAutoPopulate}
            disabled={isFetching || isNonArn}
            aria-hidden={isNonArn}
            tabIndex={isNonArn ? -1 : undefined}
            className={cn('w-[9.5rem] justify-center', isNonArn && 'pointer-events-none invisible')}
          >
            <DownloadCloud className="mr-1.5 h-4 w-4" />{' '}
            {isFetching ? 'Fetching…' : 'Fetch from ARN'}
          </Button>
        </div>
      </div>

      {/* Confirmation before either switch (nothing changes until Confirm). */}
      <Dialog
        open={pendingType !== null}
        onOpenChange={(open) => {
          if (!open) setPendingType(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {askingForNonArn ? 'Switch to Non-ARN client?' : 'Switch to ARN holder?'}
            </DialogTitle>
            <DialogDescription>
              {askingForNonArn
                ? 'This client will be given a system code (NA…) in place of their ARN, the AMFI details will no longer apply, and ARN-only products cannot be sold to them.'
                : 'You will be asked for the ARN next. The system code (NA…) is replaced by it, and the AMFI details can then be fetched.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPendingType(null)}>
              Cancel
            </Button>
            <Button type="button" onClick={confirmTypeSwitch} disabled={isChangingClientType}>
              {askingForNonArn ? 'Switch to Non-ARN' : 'Switch to ARN holder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline ARN entry when converting a Non-ARN client to an ARN holder */}
      {pendingArn !== null && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-foreground">
              Enter the client&apos;s ARN to convert them to an ARN holder
            </span>
            <Input
              value={pendingArn}
              onChange={(e) => setPendingArn(e.target.value)}
              placeholder="1–6 digits"
              maxLength={10}
              className="h-8 w-40"
            />
            {arnError && <span className="text-xs text-destructive">{arnError}</span>}
          </div>
          <Button size="sm" onClick={applyArnConversion} disabled={isChangingClientType}>
            Convert
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setPendingArn(null);
              setArnError(null);
            }}
          >
            Cancel
          </Button>
        </div>
      )}

      <Section title={isNonArn ? 'Client Information' : 'ARN Information'}>
        <Field
          label={isNonArn ? 'Client Name' : 'ARN Holder Name'}
          value={form.arnHolderName}
          onChange={(v) => setField('arnHolderName', v)}
        />
        {!isNonArn && (
          <Field
            label="ARN AUM (in Crores)"
            value={form.arnAum}
            onChange={(v) => setField('arnAum', v)}
          />
        )}
        {/*
          Fix 1: AMFI's own answers about this ARN. `readOnly`, and given a
          no-op onChange because there is nothing for it to write to. That is
          not merely a UI convention: neither field exists on any create/update
          payload, so the only thing that can change what they say is Fetch from
          ARN above. Hidden for a Non-ARN client, whose 'NA…' code is not an
          AMFI registration and has neither fact.
        */}
        {!isNonArn && (
          <Field
            label="KYD Compliant (from AMFI)"
            value={kydLabel(kydCompliant)}
            onChange={() => {}}
            readOnly
          />
        )}
        {!isNonArn && (
          <Field
            label="ARN Valid Date (from AMFI)"
            value={formatValidTill(arnValidTill)}
            onChange={() => {}}
            readOnly
          />
        )}
        <Field
          label="Address"
          value={form.arnAddress}
          onChange={(v) => setField('arnAddress', v)}
          className="md:col-span-2"
        />
      </Section>

      <Section title="Contact">
        <Field
          label="Primary Number"
          value={form.primaryNumber}
          onChange={(v) => setField('primaryNumber', v)}
          digitsOnly
          maxLength={10}
        />
        <Field
          label="Secondary Number"
          value={form.secondaryNumber}
          onChange={(v) => setField('secondaryNumber', v)}
          digitsOnly
          maxLength={10}
        />
        <Field
          label="Primary Email"
          value={form.primaryEmail}
          onChange={(v) => setField('primaryEmail', v)}
        />
        <Field
          label="Secondary Email"
          value={form.secondaryEmail}
          onChange={(v) => setField('secondaryEmail', v)}
        />
        <Field
          label="Official Company Website URL"
          value={form.websiteUrl}
          onChange={(v) => setField('websiteUrl', v)}
          className="md:col-span-2"
        />
      </Section>

      {/* 6-column grid so each row's two/three fields can be sized independently
          while staying in their existing rows (Fix 1): GSTIN narrow + Legal wide,
          Display wide + Pincode narrow, City slightly narrow + Address wide.
          The xl: count is required, not redundant: twMerge only dedupes classes
          sharing a variant prefix, so without it Section's base xl:grid-cols-3
          survives and collapses this grid to 3 tracks at >=1280px. */}
      <Section
        title="Business"
        gridClassName="md:grid-cols-6 lg:grid-cols-6 xl:grid-cols-6"
        action={
          <Button
            size="sm"
            onClick={onAutoPopulateGst}
            disabled={isFetchingGst}
            className="w-[9.5rem] justify-center bg-[#2F92FC] text-white hover:bg-[#2F92FC]/90"
          >
            <DownloadCloud className="mr-1.5 h-4 w-4" />{' '}
            {isFetchingGst ? 'Fetching…' : 'Fetch from GST'}
          </Button>
        }
      >
        <Field
          label="GSTIN"
          value={form.gstin}
          onChange={(v) => setField('gstin', v)}
          className="md:col-span-2"
        />
        <Field
          label="Legal Name of Business"
          value={form.legalName}
          onChange={(v) => setField('legalName', v)}
          className="md:col-span-4"
        />
        <Field
          label="Display Name of Business"
          value={form.displayName}
          onChange={(v) => setField('displayName', v)}
          className="md:col-span-3"
        />
        <Field
          label="Pincode"
          value={form.pincode}
          onChange={(v) => setField('pincode', v)}
          className="md:col-span-1"
        />
        <StateCitySelect
          state={form.state ?? ''}
          city={form.city ?? ''}
          onStateChange={(v) => setField('state', v)}
          onCityChange={(v) => setField('city', v)}
          stateClassName="md:col-span-2"
          cityClassName="md:col-span-2"
        />
        <Field
          label="Address"
          value={form.address}
          onChange={(v) => setField('address', v)}
          className="md:col-span-4"
        />
      </Section>

      <Section title="Languages">
        <Field label="Language 1" value={form.lang1} onChange={(v) => setField('lang1', v)} />
        <Field label="Language 2" value={form.lang2} onChange={(v) => setField('lang2', v)} />
        <Field label="Language 3" value={form.lang3} onChange={(v) => setField('lang3', v)} />
      </Section>

      <div className="flex flex-col gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Remarks
        </h4>
        <textarea
          value={form.remarks}
          onChange={(e) => setField('remarks', e.target.value)}
          rows={3}
          placeholder="Add any notes or remarks about this lead…"
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      </div>

      <div className="flex justify-end pt-2">
        <Button onClick={onSaveProceed}>{isDone ? 'Update' : 'Save and Proceed'}</Button>
      </div>
    </div>
  );
}
